import { useState } from 'react';

import { useTranslate } from '@spazious/dollhouse-i18n';
import { useStore } from '@spazious/dollhouse-store';
import { UserSettings } from '@spazious/dollhouse-types';
import {
  Button,
  ButtonProps,
  Checkbox,
  CheckboxProps,
  DialogActions,
  DialogActionsProps,
  Flex,
} from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

export interface ConfirmDialogActionsProps extends DialogActionsProps {
  setting?: keyof UserSettings;
  /**
   * Most settings gated by this dialog follow the "ask before X" polarity,
   * where `true` means "keep asking" and checking the box persists `false`.
   * Some settings (e.g. `ignoreRestrictedAreas`) are inverted: `true` means
   * "stop asking". Set this when wiring one of those up.
   */
  invertedSetting?: boolean;
  onConfirm?: (evt: {}) => void;
  onClose?: (
    evt: {},
    reason: 'backdropClick' | 'escapeKeyDown' | 'btnCancelClick' | 'btnConfirmClick'
  ) => void;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  showCloseButton?: boolean;
}

export const ConfirmDialogActions: React.FC<ConfirmDialogActionsProps> = ({
  setting,
  invertedSetting = false,
  onConfirm,
  onClose,
  confirmButtonLabel = 'Confirm',
  cancelButtonLabel = 'Cancel',
  showCloseButton = false,
}) => {
  const translate = useTranslate();
  const set = useStore(({ set }) => set);
  const askCheckbox = useStore(({ settings }) => (setting ? settings[setting] : null));
  const [askCheckboxChecked, setAskCheckboxChecked] = useState(
    invertedSetting ? Boolean(askCheckbox) : !askCheckbox
  );

  const handleConfirm = useEventCallback<PickProp<ButtonProps, 'onClick'>>((evt) => {
    onConfirm?.(evt);
    onClose?.(evt, 'btnConfirmClick');
    if (askCheckboxChecked && setting) {
      set(({ settings }) => ({
        settings: { ...settings, [setting]: invertedSetting },
      }));
    }
  });

  const handleCancel = useEventCallback<PickProp<ButtonProps, 'onClick'>>((evt) => {
    onClose?.(evt, 'btnCancelClick');
  });

  const handleAskCheckboxChange = useEventCallback<PickProp<CheckboxProps, 'onChange'>>((evt) => {
    setAskCheckboxChecked(evt.target.checked);
  });

  return (
    <DialogActions
      style={{
        justifyContent: askCheckbox ? 'space-between' : 'flex-end',
      }}
    >
      {askCheckbox !== null && (
        <Checkbox
          checked={askCheckboxChecked}
          label={translate("Don't ask me again in the future")}
          onChange={handleAskCheckboxChange}
        />
      )}
      <Flex gap="var(--spacing-gap)">
        {showCloseButton && <Button onClick={handleCancel}>{cancelButtonLabel}</Button>}
        {/* Autofocus so the confirm action can be triggered with Enter without
            reaching for the mouse (the modal remounts its content on open, so
            this fires each time the dialog is shown). */}
        <Button autoFocus color="primary" onClick={handleConfirm}>
          {confirmButtonLabel}
        </Button>
      </Flex>
    </DialogActions>
  );
};
